import { useState } from 'react'
import { CircleSlash, FileOutput, Package, ShieldCheck, ShieldX } from 'lucide-react'
import { NOT_ENTITLED, get } from '../api'
import { bytes, dateTime, shortHash, words } from '../format'
import { Button, Chip, Panel, Refusal, Spinner } from '../ui'
import { useRoleLabels } from '../roleLabels'

// ======================================================================
// THE REPORT
// ======================================================================
//
// THERE IS NO SUMMARY, NO CONCLUSION, NO SCORE AND NO RECOMMENDATION IN
// THIS FILE, AND NONE MAY BE ADDED.
//
// er_cases/report.py refuses to produce one and explains why at length.
// The same reasoning applies with more force to the renderer, because
// this is the layer where "just a one-paragraph summary at the top"
// looks like a kindness to whoever has to read forty pages. It is not:
//
//   the reader anchors on it, and the underlying material becomes
//   something they skim to confirm it;
//   it is disclosable, and it is the employer's own document
//   characterising its own case;
//   nobody wrote it, so nobody can be asked to justify it.
//
// So this component renders sections in order and adds nothing. No
// counts described as "coverage", no progress ring, no traffic lights,
// no "N of M allegations substantiated".
//
// ----------------------------------------------------------------------
// GAP CODES ARE RENDERED AS PLAIN STATEMENTS OF ABSENCE
// ----------------------------------------------------------------------
// A code is a fact. "This allegation is unsupported" is a judgement, and
// the difference is the whole point of the backend module. Every
// sentence below states what is not there and stops:
//
//     allegation_no_evidence  ->  "No evidence is attached to this
//                                  allegation."
//                             NOT "This allegation is unsupported."
//                             NOT "weak", "incomplete", "at risk".
//
// An UNRECOGNISED code renders as the raw code rather than an invented
// sentence. A new gap code added to the backend must show up as
// something a reader can look up, not as a sentence this file guessed.

const GAP_SENTENCES = {
  allegation_never_disclosed: 'This allegation has not been put to the subject.',
  allegation_not_pursued: 'This allegation is not being pursued.',
  allegation_no_evidence: 'No evidence is attached to this allegation.',
  allegation_no_finding_original:
    'No finding has been recorded on this allegation at the original stage.',
  allegation_no_finding_appeal:
    'No finding has been recorded on this allegation at the appeal stage.',
  meeting_no_notes: 'No notes have been written for this meeting.',
  meeting_notes_never_sent:
    'The notes for this meeting have not been sent to the person interviewed.',
  meeting_notes_awaiting_response:
    'Notes have been sent for this meeting and no response has been received.',
  case_no_allegations: 'No allegations are recorded on this case.',
  case_no_evidence: 'No evidence is attached to this case.',
  case_no_meetings: 'No meetings are recorded on this case.',
}

function Gaps({ codes }) {
  if (!codes?.length) return null
  return (
    <ul className="mt-2 space-y-1">
      {codes.map((code) => (
        <li key={code} className="flex items-start gap-2 text-[12px] text-muted">
          <CircleSlash size={13} strokeWidth={1.8} className="mt-0.5 shrink-0 text-faint" />
          <span>{GAP_SENTENCES[code] ?? <span className="mono">{code}</span>}</span>
        </li>
      ))}
    </ul>
  )
}

export default function Report({ caseId, canExportBundle }) {
  // The tenant's own word for each role (task 27). The report is a
  // document a tribunal may read, and it should use the words the
  // organisation uses everywhere else in the product.
  const { label: roleLabel } = useRoleLabels()
  const [doc, setDoc] = useState(null)
  const [bundle, setBundle] = useState(null)
  const [loading, setLoading] = useState(null)
  const [error, setError] = useState(null)

  // COMPILED ON REQUEST, NOT ON PAGE LOAD. GET /report writes a
  // report_generated row and GET /bundle writes bundle_exported —
  // "generating the pack that goes to a hearing is an act worth
  // recording". Fetching either automatically would put a row in the
  // chain every time somebody opened the case, which is both untrue and
  // exactly the noise that buries the rows that matter.
  const compile = async (what) => {
    setLoading(what)
    setError(null)
    try {
      const res = await get(`/${caseId}/${what}`)
      if (res === NOT_ENTITLED) return
      if (what === 'report') setDoc(res)
      else setBundle(res)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(null)
    }
  }

  return (
    <Panel
      eyebrow="A compilation of the record"
      title="Case report"
      right={
        <>
          {canExportBundle && (
            <Button
              size="sm"
              icon={Package}
              busy={loading === 'bundle'}
              onClick={() => compile('bundle')}
            >
              Export bundle
            </Button>
          )}
          <Button
            size="sm"
            variant={doc ? 'default' : 'primary'}
            icon={FileOutput}
            busy={loading === 'report'}
            onClick={() => compile('report')}
          >
            {doc ? 'Recompile' : 'Compile the report'}
          </Button>
        </>
      }
    >
      <Refusal error={error} onDismiss={() => setError(null)} />

      {!doc && !bundle && loading == null && (
        <p className="measure text-[12.5px] leading-relaxed text-muted">
          The report gathers what is on the case into one document: the parties,
          the allegations with their evidence and findings, the chronology, the
          meetings and their notes, the disclosure register and any working
          notes that were committed. It contains no summary and no conclusion —
          it compiles the record, it does not assess it. Compiling it is
          recorded against the case.
        </p>
      )}

      {loading && <Spinner label="Compiling" />}

      {bundle && <BundleView bundle={bundle} />}
      {doc && <ReportView doc={doc} />}
    </Panel>
  )
}

function ReportView({ doc }) {
  // The tenant's own word for each role (task 27). The report is a
  // document a tribunal may read; it uses the words the organisation
  // uses everywhere else in the product.
  const { label: roleLabel } = useRoleLabels()

  return (
    <article className="mt-4 space-y-6">
      <header className="border-b border-line pb-4">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="mono text-accent">{doc.case.reference}</span>
          <h3 className="font-head text-[17px] font-bold">{doc.case.title}</h3>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Chip>{doc.case.case_type_label}</Chip>
          <Chip tone="blue">{words(doc.case.current_stage)}</Chip>
          <Chip tone={doc.case.status === 'closed' ? undefined : 'accent'}>
            {words(doc.case.status)}
          </Chip>
        </div>
        <p className="mt-2 text-[11.5px] text-faint">
          Compiled {dateTime(doc.generated_at)}
          {doc.generated_by_label && <> by {doc.generated_by_label}</>} · format
          version {doc.report_format_version}
        </p>
        <Gaps codes={doc.gaps} />
      </header>

      <Section title="Parties">
        {doc.parties.length === 0 ? (
          <Absent>No parties are recorded on this case.</Absent>
        ) : (
          <ul className="space-y-1.5">
            {doc.parties.map((p) => (
              <li key={p.party_id} className="flex flex-wrap items-center gap-1.5">
                <span className="text-[12.5px]">{p.user_label || p.user_id}</span>
                <Chip>{roleLabel(p.case_role)}</Chip>
                {p.revoked_at && <Chip tone="amber">withdrawn {dateTime(p.revoked_at)}</Chip>}
                {p.conflict_declared_at && (
                  <Chip tone="amber">
                    conflict declared {dateTime(p.conflict_declared_at)}
                  </Chip>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Allegations">
        {doc.allegations.length === 0 ? (
          <Absent>No allegations are recorded on this case.</Absent>
        ) : (
          <ol className="space-y-4">
            {doc.allegations.map((a) => (
              <li key={a.allegation_id} className="panel-inset px-4 py-3.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="mono text-accent">#{a.ordinal}</span>
                  <Chip tone={a.status === 'not_pursued' ? undefined : 'accent'}>
                    {words(a.status)}
                  </Chip>
                  {a.disclosed_at && (
                    <Chip tone="blue">put to the subject {dateTime(a.disclosed_at)}</Chip>
                  )}
                </div>

                <p className="measure mt-2 whitespace-pre-wrap text-[12.5px] leading-relaxed">
                  {a.body}
                </p>

                {a.evidence.length > 0 && (
                  <div className="mt-3">
                    <div className="eyebrow mb-1.5">Evidence attached</div>
                    <ul className="space-y-1">
                      {a.evidence.map((e) => (
                        <li key={e.evidence_id} className="text-[12px] text-muted">
                          <span className="break-all text-ink">
                            {e.original_filename}
                          </span>{' '}
                          · {words(e.kind)} · {bytes(e.byte_size)} ·{' '}
                          <span className="mono" title={e.sha256}>
                            {shortHash(e.sha256)}
                          </span>
                          {e.uploaded_by_label && <> · attached by {e.uploaded_by_label}</>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {a.findings.length > 0 && (
                  <div className="mt-3">
                    <div className="eyebrow mb-1.5">Findings</div>
                    <ul className="space-y-2">
                      {a.findings.map((f) => (
                        <li
                          key={f.finding_id}
                          className="rounded-md border-l-2 border-accent-line bg-surface-2 px-3 py-2"
                        >
                          <Chip tone={f.stage === 'appeal' ? 'blue' : 'accent'}>
                            {words(f.stage)}
                          </Chip>
                          <p className="measure mt-1.5 whitespace-pre-wrap text-[12.5px] font-medium">
                            {f.finding}
                          </p>
                          {f.reasoning && (
                            <p className="measure mt-1.5 whitespace-pre-wrap text-[12px] leading-relaxed text-muted">
                              {f.reasoning}
                            </p>
                          )}
                          <p className="mt-1 text-[11px] text-faint">
                            {f.recorded_by_label && <>{f.recorded_by_label} · </>}
                            {dateTime(f.recorded_at)}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Gaps codes={a.gaps} />
              </li>
            ))}
          </ol>
        )}
      </Section>

      <Section title="Chronology">
        {doc.chronology.length === 0 ? (
          <Absent>Nothing has been recorded on this case’s timeline.</Absent>
        ) : (
          <ul className="space-y-2">
            {doc.chronology.map((t) => (
              <li key={t.transition_id} className="text-[12.5px]">
                <span className="text-faint">{dateTime(t.created_at)} — </span>
                {t.moved_stage ? (
                  <span>
                    {t.from_stage ? words(t.from_stage) : 'opened'} →{' '}
                    {words(t.to_stage)}
                  </span>
                ) : (
                  <span>recorded change</span>
                )}
                {(t.target_before || t.target_after) &&
                  t.target_before !== t.target_after && (
                    <span className="text-amber">
                      {' '}
                      · target {t.target_before ? dateTime(t.target_before) : 'none'} →{' '}
                      {t.target_after ? dateTime(t.target_after) : 'none'}
                    </span>
                  )}
                {t.reason && (
                  <span className="text-muted"> · reason: {t.reason}</span>
                )}
                {t.performed_by_label && (
                  <span className="text-faint"> · {t.performed_by_label}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Meetings">
        {doc.meetings.length === 0 ? (
          <Absent>No meetings are recorded on this case.</Absent>
        ) : (
          <ul className="space-y-4">
            {doc.meetings.map((m) => (
              <li key={m.meeting_id} className="panel-inset px-4 py-3.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-head text-[13px] font-semibold">
                    {words(m.kind)}
                  </span>
                  {m.interviewee_label && <Chip tone="blue">{m.interviewee_label}</Chip>}
                </div>
                <div className="mt-1.5 text-[11.5px] text-faint">
                  {m.scheduled_at && <>Arranged for {dateTime(m.scheduled_at)} </>}
                  {m.started_at && <>· ran {dateTime(m.started_at)} </>}
                  {m.ended_at && <>to {dateTime(m.ended_at)}</>}
                </div>
                <p className="mt-1.5 text-[12px]">
                  {m.companion_offered ? (
                    m.companion_present ? (
                      <span className="text-accent">
                        Right to be accompanied offered — {m.companion_name} attended
                      </span>
                    ) : (
                      <span className="text-accent">
                        Right to be accompanied offered, and declined
                      </span>
                    )
                  ) : (
                    <span className="text-amber">
                      No record that the right to be accompanied was offered
                    </span>
                  )}
                </p>
                {m.adjustments_note && (
                  <p className="mt-1.5 text-[12px] text-muted">
                    Adjustments: {m.adjustments_note}
                  </p>
                )}

                {m.note_versions.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {m.note_versions.map((v) => (
                      <div
                        key={v.note_id}
                        className="rounded-md border border-line bg-surface-2 px-3 py-2"
                      >
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Chip>version {v.version}</Chip>
                          {v.sent_at ? (
                            v.responded_at ? (
                              <Chip tone={v.response === 'confirmed' ? 'accent' : 'amber'}>
                                {v.response === 'confirmed' ? 'confirmed' : 'commented on'}{' '}
                                {dateTime(v.responded_at)}
                              </Chip>
                            ) : (
                              <Chip tone="amber">sent, no response</Chip>
                            )
                          ) : (
                            <Chip>not sent</Chip>
                          )}
                        </div>
                        <p className="measure mt-1.5 whitespace-pre-wrap text-[12px] leading-relaxed">
                          {v.body}
                        </p>
                        {v.comment && (
                          <div className="mt-2 border-l-2 border-amber-line pl-2.5">
                            <div className="eyebrow mb-0.5">Response</div>
                            <p className="measure whitespace-pre-wrap text-[12px] leading-relaxed">
                              {v.comment}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <Gaps codes={m.gaps} />
              </li>
            ))}
          </ul>
        )}
      </Section>

      {doc.evidence_unlinked.length > 0 && (
        <Section title="Evidence not linked to an allegation">
          <ul className="space-y-1">
            {doc.evidence_unlinked.map((e) => (
              <li key={e.evidence_id} className="text-[12px] text-muted">
                <span className="break-all text-ink">{e.original_filename}</span> ·{' '}
                {words(e.kind)} · {bytes(e.byte_size)} ·{' '}
                <span className="mono" title={e.sha256}>
                  {shortHash(e.sha256)}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Disclosure register">
        {doc.disclosures.length === 0 ? (
          <Absent>Nothing has been disclosed on this case.</Absent>
        ) : (
          <ul className="space-y-1.5">
            {doc.disclosures.map((d) => (
              <li key={d.disclosure_id} className="text-[12.5px]">
                <span className="text-faint">{dateTime(d.sent_at)} — </span>
                {d.allegation_ordinal != null
                  ? `allegation #${d.allegation_ordinal}`
                  : d.original_filename}{' '}
                <span className="text-muted">
                  to {d.recipient_label || d.recipient_id}
                </span>
                {d.redacted && (
                  <span className="text-amber"> · sent as a redacted version</span>
                )}
                {d.opened ? (
                  <span className="text-faint">
                    {' '}
                    · opened {dateTime(d.first_opened_at)}
                  </span>
                ) : (
                  <span className="text-faint"> · not yet opened</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {doc.committed_working_notes.length > 0 && (
        <Section title="Working notes committed to the record">
          <ul className="space-y-2">
            {doc.committed_working_notes.map((n) => (
              <li key={n.working_note_id} className="panel-inset px-3.5 py-3">
                <p className="measure whitespace-pre-wrap text-[12.5px] leading-relaxed">
                  {n.body}
                </p>
                <p className="mt-1.5 text-[11px] text-faint">
                  {n.authored_by_label && <>{n.authored_by_label} · </>}
                  {dateTime(n.created_at)}
                </p>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </article>
  )
}

function BundleView({ bundle }) {
  return (
    <div className="mt-4 space-y-4">
      <div
        className={`flex items-start gap-3 rounded-md border px-4 py-3 ${
          bundle.chain.verified
            ? 'border-accent-line bg-accent-dim'
            : 'border-danger-line bg-danger-dim'
        }`}
      >
        {bundle.chain.verified ? (
          <ShieldCheck size={16} strokeWidth={1.9} className="mt-0.5 shrink-0 text-accent" />
        ) : (
          <ShieldX size={16} strokeWidth={1.9} className="mt-0.5 shrink-0 text-danger" />
        )}
        <div>
          <p
            className={`font-head text-[12.5px] font-semibold ${
              bundle.chain.verified ? 'text-accent' : 'text-danger'
            }`}
          >
            {bundle.chain.verified
              ? 'The audit chain for this case verifies'
              : `The audit chain does not verify from entry ${bundle.chain.first_bad_seq}`}
          </p>
          <p className="mt-0.5 text-[11.5px] leading-relaxed text-muted">
            Checked {dateTime(bundle.chain.verified_at)} · {bundle.chain.algorithm}
          </p>
        </div>
      </div>

      {bundle.omitted.length > 0 && (
        <div className="rounded-md border border-line bg-surface-2 px-4 py-3">
          <div className="eyebrow mb-1.5">Sections not included</div>
          <ul className="space-y-1">
            {bundle.omitted.map((o) => (
              <li key={o.section} className="text-[12px] text-muted">
                {words(o.section)} — not included, because it needs{' '}
                <span className="mono">{o.requires}</span>, which you do not hold on
                this case.
              </li>
            ))}
          </ul>
        </div>
      )}

      {bundle.evidence_manifest && (
        <div>
          <div className="eyebrow mb-2">
            Evidence manifest · {bundle.evidence_count} item
            {bundle.evidence_count === 1 ? '' : 's'}
          </div>
          <ul className="space-y-1">
            {bundle.evidence_manifest.map((e) => (
              <li key={e.evidence_id} className="text-[12px] text-muted">
                <span className="break-all text-ink">{e.original_filename}</span> ·{' '}
                {words(e.kind)} · {bytes(e.byte_size)} ·{' '}
                <span className="mono" title={e.sha256}>
                  {shortHash(e.sha256)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {bundle.audit_trail && (
        <div>
          <div className="eyebrow mb-2">
            Audit trail · {bundle.audit_row_count} entr
            {bundle.audit_row_count === 1 ? 'y' : 'ies'}
          </div>
          <ul className="max-h-72 space-y-0.5 overflow-y-auto pr-1">
            {bundle.audit_trail.map((r) => (
              <li key={r.seq} className="flex items-baseline gap-3 text-[11.5px]">
                <span className="mono w-10 shrink-0 text-right text-faint">{r.seq}</span>
                <span className="flex-1 text-muted">{words(r.event)}</span>
                <span className="text-faint">{dateTime(r.created_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {bundle.report && <ReportView doc={bundle.report} />}
    </div>
  )
}

function Section({ title, children }) {
  return (
    <section>
      <h4 className="eyebrow mb-2.5">{title}</h4>
      {children}
    </section>
  )
}

/** An absence, stated. Never characterised. */
function Absent({ children }) {
  return (
    <p className="flex items-start gap-2 text-[12px] text-muted">
      <CircleSlash size={13} strokeWidth={1.8} className="mt-0.5 shrink-0 text-faint" />
      {children}
    </p>
  )
}
